<script lang="ts" setup>
import { computed } from 'vue'

import HomeThumbnail from '@/home/HomeThumbnail.vue'
import { RevaluationTypeEnum, SearchGroup } from '@/saved-searches/types'
import { useStore } from '@/store'
import { ModeEnum, PmsiTypeEnum, SearchGroupPrivacyEnum } from '@/store/modules/settings/types'
import { StayStatusEnum } from '@/store/modules/stay/types'

const store = useStore()

const props = withDefaults(defineProps<{
  mode: string
  pmsiType?: PmsiTypeEnum
}>(), {
  pmsiType: PmsiTypeEnum.MCO
})

const settingsUrlQuery = computed(() => store.getters.settingsUrlQuery)
const iaEnabled = computed(() => store.getters.flatSancareSettings['enable_ai_suggestions'])
const isLoading = computed(() => Boolean(store.state.home.requests.getHomeStats.fetching))

const systemSearchGroup = computed<SearchGroup>(() => {
  const candidates = store.state.savedSearches.savedSearchesGroups.filter((group) => group.privacy === SearchGroupPrivacyEnum.SYSTEM)
  // caution: for now we only support a unique system group by pmsiType/mode
  return candidates[0] ?? null
})

const buttons = computed(() => {
  const stats = store.state.home.homeStats
  const buttons = []

  const queryParams = {
    mode: props.mode,
    ...settingsUrlQuery.value
  }

  switch (props.mode) {
    case ModeEnum.NEW_CODING:
      buttons.push({
        name: 'newCodingShortStays',
        count: stats.countTodoNewCodingTotalStay,
        label: 'Tous les séjours',
        stayListQuery: { ...queryParams, newCoding: true },
      })
      buttons.push({
        name: 'newCodingValidatedStays',
        count: stats.countDoneNewCodingTotalStay,
        label: 'Séjours validés',
        stayListQuery: { ...queryParams, newCoding: true, sss: [StayStatusEnum.VALIDATED] },
      })
      break
    case ModeEnum.DIALYSIS:
      buttons.push({
        name: 'allDialysisStays',
        count: stats.countTodoDialysisStay,
        label: 'Tous les séjours',
        stayListQuery: queryParams,
      })
      break
    case ModeEnum.QUALITY_STAYS:
      buttons.push({
        name: 'allQualityStays',
        count: stats.countTodoQualityStay,
        label: 'Tous les séjours',
        stayListQuery: queryParams,
      })
      buttons.push({
        name: 'newCodingQualityStays',
        count: stats.countTodoNewCodingQualityStay,
        label: 'Séjours sans codage établissement',
        stayListQuery: { ...queryParams, newCoding: true },
      })
      break
    case ModeEnum.OPTIMIZABLE:
      buttons.push({
        name: 'AllDasRevaluables',
        count: stats.countTodoDasRevaluableStay,
        label: 'Séjours optimisables (DAS)',
        stayListQuery: { ...queryParams, revaluationType: RevaluationTypeEnum.IA_OR_SEARCH_DAS },
      })
      switch (props.pmsiType) {
        case (PmsiTypeEnum.SMR):
          buttons.push({
            name: 'MedicalStaysRevaluable',
            // TODO: display the stat when it is ready
            count: '-',
            label: 'Séjours optimisables (Médical)',
            stayListQuery: { ...queryParams, revaluationType: '' },
            disabled: true,
          })
          buttons.push({
            name: 'RehabilitationStaysRevaluable',
            // TODO: display the stat when it is ready
            count: '-',
            label: 'Séjours optimisables (Rééducation)',
            stayListQuery: { ...queryParams, revaluationType: '' },
            disabled: true,
          })
          buttons.push({
            name: 'DependencyStaysRevaluable',
            // TODO: display the stat when it is ready
            count: '-',
            label: 'Séjours optimisables (Dépendances)',
            stayListQuery: { ...queryParams, revaluationType: '' },
            disabled: true,
          })
          break
        case (PmsiTypeEnum.MCO):
        default:
          buttons.push({
            name: 'AllChronicDasRevaluables',
            // TODO: display the stat when it is ready
            count: '-',
            label: 'Séjours optimisables (DAS chronique)',
            stayListQuery: { ...queryParams, revaluationType: RevaluationTypeEnum.CHRONIC_DAS },
          })
          buttons.push({
            name: 'AllDasIaRevaluables',
            count: stats.countTodoDasIaRevaluableStay,
            label: 'Séjours optimisables par IA (DAS)',
            stayListQuery: { ...queryParams, revaluationType: RevaluationTypeEnum.IA_DAS },
            disabled: !iaEnabled.value,
          })
          buttons.push({
            name: 'AllDpIaRevaluables', 
            count: stats.countTodoDpIaRevaluableStay,
            label: 'Séjours optimisables par IA (DP)',
            stayListQuery: { ...queryParams, revaluationType: RevaluationTypeEnum.IA_DP },
            disabled: !iaEnabled.value,
          })
      }
      if (systemSearchGroup.value?.savedSearches?.length > 0) {
        buttons.push({
          name: 'countTodoFavoriteRevaluableStay',
          count: systemSearchGroup.value.countTodoStay,
          label: 'Palmarès Sancare',
          stayListQuery: { ...queryParams, searchId: systemSearchGroup.value.savedSearches.map((search) => search.id) },
          color: 'gold',
          icon: 'feed-trophy'
        })
      }
      break
  }

  return buttons
})
</script>

<template>
  <div class="row g-0 home-list-part border-0">
    <div
      v-for="button in buttons"
      :key="button.name"
      class="col-3 p-2"
    >
      <home-thumbnail
        :count="button.count"
        :label="button.label"
        :name="button.name"
        :stay-list-query="button.stayListQuery"
        :pmsi-type="pmsiType"
        :disabled="Boolean(button.disabled)"
        :clickable="!Boolean(button.disabled)"
        :color="button.color ?? 'uniform'"
        :icon="button.icon ?? null"
        :loading="isLoading && !button.disabled"
      />
    </div>
  </div>
</template>
